var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Admin, ComponentPropType, Error as DefaultError, Loading, TranslationProvider, defaultI18nProvider, } from 'react-admin';
import { createHashHistory, createMemoryHistory } from 'history';
import { createTheme as /* tree-shaking no-side-effects-when-called */ createMuiTheme } from '@material-ui/core';
import ErrorBoundary from './ErrorBoundary';
import IntrospectionContext from './IntrospectionContext';
import ResourceGuesser from './ResourceGuesser';
import SchemaAnalyzerContext from './SchemaAnalyzerContext';
import { Layout } from './layout';
import introspectReducer from './introspectReducer';
const displayOverrideCode = (resources) => {
    if (process.env.NODE_ENV === 'production')
        return;
    let code = 'If you want to override at least one resource, paste this content in the <AdminGuesser> component of your app:\n\n';
    resources.forEach((r) => {
        code += `<ResourceGuesser name={"${r.name}"} />\n`;
    });
    // eslint-disable-next-line no-console
    console.info(code);
};
/**
 * AdminResourcesGuesser automatically renders an `<AdminUI>` component for resources exposed by a web API documented with Hydra, OpenAPI or any other format supported by `@api-platform/api-doc-parser`.
 * If child components are passed (usually `<ResourceGuesser>` or `<Resource>` components, but it can be any other React component), they are rendered in the given order.
 * If no children are passed, a `<ResourceGuesser>` component is created for each resource type exposed by the API, in the order they are specified in the API documentation.
 */
export const AdminResourcesGuesser = (_a) => {
    var { 
    // Admin props
    customReducers = {}, loadingPage: LoadingPage = Loading, admin: AdminEl = Admin, 
    // Props
    children, includeDeprecated, resources, loading } = _a, rest = __rest(_a, ["customReducers", "loadingPage", "admin", "children", "includeDeprecated", "resources", "loading"]);
    if (loading) {
        return React.createElement(LoadingPage, null);
    }
    let resourceChildren = children;
    if (!resourceChildren && resources) {
        const guessResources = includeDeprecated
            ? resources
            : resources.filter((r) => !r.deprecated);
        resourceChildren = guessResources.map((r) => (React.createElement(ResourceGuesser, { name: r.name, key: r.name })));
        displayOverrideCode(guessResources);
    }
    return (React.createElement(AdminEl, Object.assign({ customReducers: Object.assign({ introspect: introspectReducer }, customReducers), loading: LoadingPage }, rest), resourceChildren));
};
const defaultTheme = createMuiTheme({
    palette: {
        primary: {
            contrastText: '#ffffff',
            main: '#38a9b4',
        },
        secondary: {
            main: '#288690',
        },
    },
});
const AdminGuesser = (_a) => {
    var { 
    // Props for SchemaAnalyzerContext
    schemaAnalyzer, 
    // Props for AdminResourcesGuesser
    includeDeprecated = false, 
    // Admin props
    dataProvider, history, customRoutes = [], layout = Layout, loading: loadingPage, theme = defaultTheme, 
    // Other props
    children } = _a, rest = __rest(_a, ["schemaAnalyzer", "includeDeprecated", "dataProvider", "history", "customRoutes", "layout", "loading", "theme", "children"]);
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState();
    const [addedCustomRoutes, setAddedCustomRoutes] = useState([]);
    const [introspect, setIntrospect] = useState(true);
    let adminHistory = history;
    if (!adminHistory) {
        adminHistory =
            typeof window === 'undefined'
                ? createMemoryHistory()
                : createHashHistory();
    }
    useEffect(() => {
        if (typeof dataProvider.introspect !== 'function') {
            throw new Error('The given dataProvider needs to expose an "introspect" function returning a parsed API documentation from api-doc-parser');
        }
        if (!introspect) {
            return;
        }
        dataProvider
            .introspect()
            .then(({ data, customRoutes: introspectCustomRoutes = [] }) => {
            var _a;
            setResources((_a = data.resources) !== null && _a !== void 0 ? _a : []);
            setAddedCustomRoutes(introspectCustomRoutes);
            setIntrospect(false);
            setLoading(false);
        })
            .catch((error) => {
            // Allow error to be caught by the error boundary
            setError(() => {
                throw error;
            });
        });
    }, [introspect, dataProvider]);
    const introspectionContext = useMemo(() => ({
        introspect: () => {
            setLoading(true);
            setIntrospect(true);
        },
    }), [setLoading, setIntrospect]);
    return (React.createElement(IntrospectionContext.Provider, { value: introspectionContext },
        React.createElement(SchemaAnalyzerContext.Provider, { value: schemaAnalyzer },
            React.createElement(AdminResourcesGuesser, Object.assign({ includeDeprecated: includeDeprecated, resources: resources, loading: loading, dataProvider: dataProvider, history: adminHistory, customRoutes: [...addedCustomRoutes, ...customRoutes], layout: layout, loadingPage: loadingPage, theme: theme }, rest), children))));
};
/* eslint-disable tree-shaking/no-side-effects-in-initialization */
AdminGuesser.propTypes = {
    dataProvider: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
        .isRequired,
    authProvider: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    i18nProvider: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    history: PropTypes.object,
    customReducers: PropTypes.object,
    customSagas: PropTypes.array,
    initialState: PropTypes.object,
    schemaAnalyzer: PropTypes.object.isRequired,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    theme: PropTypes.object,
    includeDeprecated: PropTypes.bool,
    customRoutes: PropTypes.array,
};
/* eslint-enable tree-shaking/no-side-effects-in-initialization */
const AdminGuesserWithError = (_a) => {
    var { error } = _a, props = __rest(_a, ["error"]);
    if (!props.i18nProvider) {
        throw new Error('Missing i18nProvider');
    }
    return (React.createElement(TranslationProvider, { i18nProvider: props.i18nProvider },
        React.createElement(ErrorBoundary, { error: error },
            React.createElement(AdminGuesser, Object.assign({}, props)))));
};
AdminGuesserWithError.defaultProps = {
    error: DefaultError,
    i18nProvider: defaultI18nProvider,
};
AdminGuesserWithError.propTypes = {
    error: ComponentPropType,
};
export default AdminGuesserWithError;
//# sourceMappingURL=AdminGuesser.js.map